import React from 'react';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import axios from 'axios';
import { Box, Container, Card, CardContent, Stack, CircularProgress, Chip, useTheme } from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import CitySearch from '../components/forms/CitySearch';
import { useTranslation } from 'react-i18next';
import { HoraResult, HoraSlot, currentHora, shiftDate, todayInZone } from '../utils/hora';
import { horaAppearance, horaCopy, horaProfile, horaTime } from '../i18n/horaCatalog';

type City = { id: string; name: string; displayName: string; coordinates: { latitude: number; longitude: number }; timezone: { name: string; offset: number }; population: number };
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
export default function Hora() {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const { ui } = horaCopy(i18n.language);
  const [city, setCity] = React.useState<City | null>(null);
  const [date, setDate] = React.useState(() => todayInZone('Asia/Kolkata'));
  const [data, setData] = React.useState<HoraResult | null>(null);
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [expandedSlot, setExpandedSlot] = React.useState('');
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => { document.title = `${ui.title} - Kundlistar`; }, [ui.title]);
  React.useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(timer); }, []);
  React.useEffect(() => {
    setData(null); setError(false); setExpandedSlot('');
    if (!city || !date) { setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);
    axios.get(`${API_BASE_URL}/hora`, { signal: controller.signal, params: { date, lat: city.coordinates.latitude, lng: city.coordinates.longitude, timezone: city.timezone.name } })
      .then(response => { if (!controller.signal.aborted) setData(response.data.data); })
      .catch(() => { if (!controller.signal.aborted) setError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [city, date]);
  const active = data ? currentHora(data, now) : null;
  const timezone = data?.timezone || city?.timezone.name || 'Asia/Kolkata';
  const time = (value: string) => horaTime(value, timezone, i18n.language, date);
  const row = (slot: HoraSlot, detailed = false) => {
    const profile = horaProfile(slot.planet, i18n.language);
    const expanded = detailed || expandedSlot === slot.start;
    const appearance = horaAppearance(slot.planet, theme.palette.mode === 'dark');
    return <Box key={slot.start} sx={{ p: 1.5, m: 1, border: 1, borderRadius: 2, borderColor: appearance.color, bgcolor: active?.start === slot.start ? appearance.background : undefined, borderLeft: active?.start === slot.start ? '4px solid' : undefined }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
        <Typography fontWeight={700}>{profile?.name || slot.planet}{active?.start === slot.start ? ` • ${ui.currentTag}` : ''}</Typography>
        <Typography>{time(slot.start)} – {time(slot.end)}</Typography>
      </Stack>
      {profile && <Chip variant="outlined" label={profile.quality} aria-expanded={detailed ? undefined : expanded} aria-label={`${profile.quality}${detailed ? '' : `: ${expanded ? ui.hideDetails : ui.details}`}`}
        icon={detailed ? undefined : expanded ? <ExpandLess /> : <ExpandMore />}
        onClick={detailed ? undefined : () => setExpandedSlot(expanded ? '' : slot.start)}
        sx={{ mt: 1, minHeight: 40, height: 'auto', maxWidth: '100%', color: appearance.color, borderColor: appearance.color, bgcolor: appearance.background, fontWeight: 700, '& .MuiChip-icon': { color: appearance.color }, '& .MuiChip-label': { whiteSpace: 'normal', py: 1 }, '&:hover, &:focus-visible': { bgcolor: appearance.background, outline: `2px solid ${appearance.color}`, outlineOffset: 2 } }} /> }
      {expanded && profile && <Stack spacing={1} sx={{ mt: 1 }}>
        <Typography><strong>{ui.nature}: </strong>{profile.nature}</Typography>
        <Typography><strong>{ui.activities}: </strong>{profile.activities}</Typography>
        <Typography color="text.secondary"><strong>{ui.caution}: </strong>{profile.caution}</Typography>
      </Stack>}
    </Box>;
  };
  return <Container maxWidth="md" sx={{ py: 4 }}>
    <Typography variant="h4" fontWeight={800} gutterBottom>{ui.title}</Typography>
    <Typography color="text.secondary" sx={{ mb: 2 }}>{ui.intro}</Typography>
    <Card sx={{ mb: 2 }}><CardContent><Stack spacing={2}>
      <CitySearch value={city} onChange={setCity} label={ui.place} required />
      <Stack direction="row" spacing={1} alignItems="center">
        <Button aria-label={ui.previousDate} onClick={() => setDate(shiftDate(date, -1))}>−</Button>
        <TextField fullWidth type="date" label={ui.date} value={date} onChange={event => { if (event.target.value) setDate(event.target.value); }} InputLabelProps={{ shrink: true }} />
        <Button aria-label={ui.nextDate} onClick={() => setDate(shiftDate(date, 1))}>+</Button>
        <Button onClick={() => setDate(todayInZone(timezone))}>{ui.today}</Button>
      </Stack>
    </Stack></CardContent></Card>
    {!city && <Alert severity="info">{ui.choosePlace}</Alert>}
    {loading && <CircularProgress />}
    {error && <Alert severity="error">{ui.loadError}</Alert>}
    {data && <Stack spacing={2}>
      <Card><CardContent><Typography variant="h6">{ui.current}</Typography>
        {active ? <>{row(active, true)}{Date.parse(active.start) < Date.parse(data.sunrise) && <Typography variant="caption">{ui.previousNight}</Typography>}</> : <Typography>{ui.noActive}</Typography>}
      </CardContent></Card>
      <Card><CardContent><Typography variant="h6">{ui.day}</Typography><Typography color="text.secondary">{ui.sunrise}: {time(data.sunrise)} • {ui.sunset}: {time(data.sunset)}</Typography></CardContent>{data.day.map(slot => row(slot))}</Card>
      <Card><CardContent><Typography variant="h6">{ui.night}</Typography><Typography color="text.secondary">{ui.sunset}: {time(data.sunset)} • {ui.nextSunrise}: {time(data.nextSunrise)}</Typography></CardContent>{data.night.map(slot => row(slot))}</Card>
      <Typography variant="caption" color="text.secondary">{ui.timezoneNote} ({data.timezone})</Typography>
    </Stack>}
    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{ui.disclaimer}</Typography>
  </Container>;
}
